import {
  InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, MaybeRef, unref } from "vue";

import { components as OpenAPI } from "@/autogen/openapi";
import { ok } from "@/lib/openapi";
import { useStore as useScaffoldingStore } from "@/store/stores/scaffolding";
import { SearchResultWithId } from "@/types/search_result";

import api from "../../base";
import { createBatchedFetch } from "../batch-fetch";
import { useGenericListingQuery } from "../generic";
import {
  findItemInInfiniteQueries,
  findItemInListQueries,
  useNewsletterId,
} from "../utils";
import { SubscriberPage } from "./list";

export type Subscriber = OpenAPI["schemas"]["Subscriber"];

export type SubscriberCreateInput = OpenAPI["schemas"]["SubscriberInput"];
export type SubscriberUpdateInput =
  OpenAPI["schemas"]["SubscriberUpdateInput"] & { id: string };

const fetchSubscriber = createBatchedFetch<string, Subscriber>({
  limit: 50,
  idFromResource(resource) {
    return resource.id;
  },
  async fetch(ids, signal) {
    const data = await ok(
      api.get("/subscribers", {
        signal,
        params: {
          query: {
            ids: ids,
          },
        },
      })
    );

    return data.results;
  },
});

export const useSubscriberDetailQuery = ({
  id,
  batch = false,
}: {
  id: MaybeRef<string | undefined>;
  batch?: boolean;
}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "subscribers", "detail", id],
    enabled: () => unref(id) !== undefined,
    async queryFn({ signal }) {
      if (batch) {
        return await fetchSubscriber(unref(id)!, signal);
      }

      const data = await ok(
        api.get("/subscribers/{id_or_email}", {
          signal,
          params: {
            path: {
              id_or_email: unref(id)!,
            },
            query: {},
          },
        })
      );

      return data;
    },
  });

  const data = query.data;
  const dataWithFallback = computed((): Subscriber | undefined => {
    if (data.value) {
      return data.value;
    }

    const needle = unref(id);

    {
      const queries = queryClient.getQueriesData<InfiniteData<SubscriberPage>>({
        queryKey: [newsletterId, "subscribers", "list"],
      });

      const found = findItemInInfiniteQueries<Subscriber>(
        queries,
        (item) => item.id === needle
      );

      if (found !== undefined) {
        return found;
      }
    }

    {
      const queries = queryClient.getQueriesData<SearchResultWithId[]>({
        queryKey: [newsletterId, "search"],
      });

      type SubscriberSearchResult = SearchResultWithId & { type: "subscriber" };

      // this would've looked nicer if TypeScript didn't lose the type narrowing
      // after adding the `.obj.id` condition :(
      const found = findItemInListQueries<
        SearchResultWithId,
        SubscriberSearchResult
      >(queries, (item): item is SubscriberSearchResult => {
        return item.type === "subscriber" && item.obj.id === needle;
      });

      if (found !== undefined) {
        return found.obj satisfies Subscriber;
      }
    }

    return undefined;
  });

  return { ...query, dataWithFallback };
};

export const useSubscriberReferralsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();

  const scaffoldingStore = useScaffoldingStore();
  const isEnabled = computed(() => {
    const newsletter = scaffoldingStore.newsletter;
    if (!newsletter?.enabled_features?.includes("referrals")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [newsletterId, "subscribers", "referrals", subscriberId],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/referrals", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useSubscriberStripeSubscriptionsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();

  const scaffoldingStore = useScaffoldingStore();
  const isEnabled = computed(() => {
    const newsletter = scaffoldingStore.newsletter;
    if (!newsletter?.enabled_features?.includes("paid_subscriptions")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [
      newsletterId,
      "subscribers",
      "stripe-subscriptions",
      subscriberId,
    ],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/stripe-subscriptions", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      return {
        ...data,
        results: data.results.map((item) => {
          return { ...item, id: item.subscription_id };
        }),
      };
    },
  });

  return query;
};

export const useSubscriberAutomationsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();

  const scaffoldingStore = useScaffoldingStore();
  const isEnabled = computed(() => {
    const newsletter = scaffoldingStore.newsletter;
    if (!newsletter?.enabled_features?.includes("automations")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [newsletterId, "subscribers", "automations", subscriberId],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/automations", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      console.log(`/subscribers/${unref(subscriberId)}/automations`);
      console.log(data);

      return data;
    },
  });

  return query;
};

export const useCreateSubscriberMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn(input: SubscriberCreateInput): Promise<Subscriber> {
      const data = await ok(
        api.post("/subscribers", {
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "subscribers", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "subscribers", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};

export const useUpdateSubscriberMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({
      id,
      ...input
    }: SubscriberUpdateInput): Promise<Subscriber> {
      const data = await ok(
        api.patch("/subscribers/{id_or_email}", {
          params: {
            path: { id_or_email: id },
          },
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "subscribers", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "subscribers", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};

export const useDeleteSubscriberMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({ id }: { id: string }): Promise<void> {
      await ok(
        api.delete("/subscribers/{id_or_email}", {
          params: {
            path: { id_or_email: id },
          },
        })
      );
    },
    onSuccess(_data, { id }) {
      queryClient.resetQueries({
        queryKey: [newsletterId, "subscribers", "detail", id],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "subscribers", "list"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};
